import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { matchesSelectSearch } from "./selectSearch";

export interface YhMultiSelectOption {
  label: string;
  value: string | number;
}

export interface YhMultiSelectProps {
  label?: string;
  error?: string;
  options: YhMultiSelectOption[];
  /** Selected values */
  value?: Array<string | number>;
  placeholder?: string;
  requiredMark?: boolean;
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  showOptionIcon?: boolean;
  showSelectAll?: boolean;
  /** Max chips to show before collapsing into +N (default 2). Use 0 to show count only. */
  maxVisibleChips?: number;
  /** Show a single clear (×) when any value is selected */
  clearable?: boolean;
  /** Called with the full next selected values array */
  onChange?: (values: Array<string | number>) => void;
  /** Fires when the dropdown opens or closes */
  onOpenChange?: (open: boolean) => void;
  /** Fires as the user types in the search box (for async/remote search) */
  onSearchChange?: (query: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
}

const PeopleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`transition-transform ${open ? "rotate-180" : ""}`}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const ClearIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const CheckIcon = ({ className = "" }: { className?: string }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    className={className}
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

type MenuPos = { top: number; left: number; width: number; maxHeight: number };

/**
 * Global multi-select with chips, search, select-all, and clear-all.
 *
 * @example
 * <YhMultiSelect
 *   label="Assigned To"
 *   placeholder="Select members..."
 *   options={[{ label: "Tech", value: "1" }]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 */
export const YhMultiSelect: React.FC<YhMultiSelectProps> = ({
  label,
  error,
  options = [],
  value = [],
  placeholder = "Select...",
  requiredMark,
  className = "",
  id,
  name,
  disabled,
  searchable = true,
  searchPlaceholder,
  showOptionIcon = true,
  showSelectAll = true,
  maxVisibleChips = 2,
  clearable = false,
  onChange,
  onOpenChange,
  onSearchChange,
  onBlur,
  onFocus,
}) => {
  const reactId = useId();
  const selectId = id || name || reactId;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const skipOpenNotify = useRef(true);

  const selectedSet = useMemo(
    () => new Set((value || []).map((v) => String(v))),
    [value]
  );

  const selectedOptions = useMemo(() => {
    const byValue = new Map(
      options.map((o) => [String(o.value), o] as const)
    );
    return (value || [])
      .map((v) => {
        const key = String(v);
        return (
          byValue.get(key) || {
            label: key,
            value: v,
          }
        );
      })
      .filter((o) => o && String(o.value));
  }, [options, value]);

  const selectedCount = selectedOptions.length;

  const visibleChips = useMemo(() => {
    if (maxVisibleChips <= 0) return [];
    return selectedOptions.slice(0, maxVisibleChips);
  }, [selectedOptions, maxVisibleChips]);

  const hiddenCount = Math.max(0, selectedCount - visibleChips.length);

  const filtered = useMemo(() => {
    if (onSearchChange) return options;
    const q = query.trim();
    if (!q) return options;
    return options.filter((o) => matchesSelectSearch(o.label, o.value, q));
  }, [options, query, onSearchChange]);

  const allFilteredSelected =
    filtered.length > 0 &&
    filtered.every((o) => selectedSet.has(String(o.value)));

  const updateMenuPos = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
    const spaceAbove = rect.top - gap - 8;
    const preferBelow = spaceBelow >= 180 || spaceBelow >= spaceAbove;
    const maxHeight = Math.min(
      320,
      Math.max(140, preferBelow ? spaceBelow : spaceAbove)
    );
    setMenuPos({
      top: preferBelow
        ? rect.bottom + gap
        : Math.max(8, rect.top - gap - maxHeight),
      left: rect.left,
      width: Math.max(rect.width, 240),
      maxHeight,
    });
  };

  useEffect(() => {
    if (skipOpenNotify.current) {
      skipOpenNotify.current = false;
      return;
    }
    onOpenChange?.(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
      onBlur?.();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReposition = () => updateMenuPos();
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, onBlur]);

  const setValues = (next: Array<string | number>) => {
    onChange?.(next);
  };

  const toggleValue = (optValue: string | number) => {
    const key = String(optValue);
    if (selectedSet.has(key)) {
      setValues((value || []).filter((v) => String(v) !== key));
    } else {
      setValues([...(value || []), optValue]);
    }
  };

  const removeValue = (optValue: string | number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setValues((value || []).filter((v) => String(v) !== String(optValue)));
  };

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredKeys = new Set(filtered.map((o) => String(o.value)));
      setValues((value || []).filter((v) => !filteredKeys.has(String(v))));
    } else {
      const merged = new Map<string, string | number>();
      (value || []).forEach((v) => merged.set(String(v), v));
      filtered.forEach((o) => merged.set(String(o.value), o.value));
      setValues(Array.from(merged.values()));
    }
  };

  const handleClearAll = () => setValues([]);

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              zIndex: 10000,
            }}
            className="flex flex-col overflow-hidden rounded-xl border border-[#EEF0F5] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)]"
          >
            {searchable ? (
              <div className="shrink-0 border-b border-[#EEF0F5] p-2.5">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
                    <SearchIcon />
                  </span>
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    autoComplete="off"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const next = e.target.value;
                      setQuery(next);
                      onSearchChange?.(next);
                    }}
                    placeholder={
                      searchPlaceholder ||
                      `Search ${label ? label.toLowerCase() : "options"}...`
                    }
                    className="w-full rounded-lg border border-[#E5E7EB] bg-white py-2 pl-9 pr-3 text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF] focus:border-[#841C39]"
                  />
                </div>
              </div>
            ) : null}

            {showSelectAll ? (
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#EEF0F5] px-3 py-2.5">
                <button
                  type="button"
                  className="flex items-center gap-2.5 text-sm font-medium text-[#1E1B4B]"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded border ${
                      allFilteredSelected
                        ? "border-[#A02448] bg-[#A02448] text-white"
                        : "border-[#D1D5DB] bg-white"
                    }`}
                  >
                    {allFilteredSelected ? <CheckIcon /> : null}
                  </span>
                  Select All
                </button>
                <span className="rounded-full bg-[#F9F0F4] px-2.5 py-0.5 text-xs font-bold text-[#A02448]">
                  {selectedCount} / {options.length}
                </span>
              </div>
            ) : null}

            <ul
              role="listbox"
              aria-multiselectable
              className="min-h-0 flex-1 overflow-y-auto py-1 [scrollbar-color:#C4B5FD_#F3F4F6] [scrollbar-width:thin]"
              style={{
                maxHeight:
                  menuPos.maxHeight -
                  (searchable ? 56 : 0) -
                  (showSelectAll ? 48 : 0) -
                  48,
              }}
            >
              {filtered.length === 0 ? (
                <li className="px-3 py-4 text-center text-sm text-[#9CA3AF]">
                  No results found
                </li>
              ) : (
                filtered.map((opt) => {
                  const isActive = selectedSet.has(String(opt.value));
                  return (
                    <li key={String(opt.value)}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition ${
                          isActive
                            ? "bg-[#F5F3FF] text-[#1E1B4B]"
                            : "text-[#1E1B4B] hover:bg-[#F8FAFC]"
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleValue(opt.value);
                        }}
                        onClick={(e) => e.preventDefault()}
                      >
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                            isActive
                              ? "border-[#A02448] bg-[#A02448] text-white"
                              : "border-[#D1D5DB] bg-white"
                          }`}
                        >
                          {isActive ? <CheckIcon /> : null}
                        </span>

                        {showOptionIcon ? (
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F9F0F4] text-[#A02448]">
                            <PeopleIcon />
                          </span>
                        ) : null}

                        <span className="min-w-0 flex-1 truncate font-medium">
                          {opt.label}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] px-3 py-2.5">
              <span className="text-sm font-semibold text-[#4338CA]">
                {selectedCount} selected
              </span>
              <button
                type="button"
                className="text-sm font-semibold text-[#A02448] hover:text-[#4338CA] disabled:opacity-40"
                onClick={handleClearAll}
                disabled={selectedCount === 0}
              >
                Clear all
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`relative w-full ${className}`} ref={rootRef}>
      {label ? (
        <label htmlFor={selectId} className="yh-label">
          {label}
          {requiredMark ? <span className="ml-0.5 text-yh-red">*</span> : null}
        </label>
      ) : null}

      <button
        ref={buttonRef}
        id={selectId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-multiselectable
        onFocus={onFocus}
        onClick={() => {
          if (!disabled) setOpen((v) => !v);
        }}
        className={`yh-input flex min-h-[46px] w-full items-center justify-between gap-2 text-left ${
          open
            ? "!border-[#841C39] shadow-[0_0_0_2px_rgba(132,28,57,0.12)]"
            : ""
        } ${error ? "yh-input-error" : ""} ${
          disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
        }`}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {selectedCount === 0 ? (
            <span className="text-[#9CA3AF]">{placeholder}</span>
          ) : (
            <>
              {visibleChips.map((opt) => (
                <span
                  key={String(opt.value)}
                  className="inline-flex max-w-[140px] items-center gap-1 rounded-lg bg-[#F9F0F4] px-2 py-1 text-xs font-semibold text-[#4338CA]"
                  title={opt.label}
                >
                  <span className="truncate">{opt.label}</span>
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={`Remove ${opt.label}`}
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-[#A02448] hover:bg-[#DDD6FE]"
                    onClick={(e) => removeValue(opt.value, e)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        removeValue(opt.value);
                      }
                    }}
                  >
                    ×
                  </span>
                </span>
              ))}
              {hiddenCount > 0 ? (
                <span className="inline-flex items-center rounded-lg bg-[#A02448] px-2 py-1 text-xs font-bold text-white">
                  +{hiddenCount}
                </span>
              ) : null}
              {maxVisibleChips <= 0 ? (
                <span className="inline-flex items-center rounded-lg bg-[#A02448] px-2.5 py-1 text-xs font-bold text-white">
                  {selectedCount} selected
                </span>
              ) : null}
            </>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-[#6B7280]">
          {clearable && selectedCount > 0 && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              title="Clear"
              className="flex h-5 w-5 items-center justify-center rounded-full text-[#9CA3AF] transition hover:bg-[#F3F4F6] hover:text-[#6B7280]"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange?.([]);
                setOpen(false);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ClearIcon />
            </span>
          ) : null}
          <ChevronIcon open={open} />
        </span>
      </button>

      {menu}

      {error ? <p className="mt-1 text-xs text-yh-red">{error}</p> : null}
    </div>
  );
};

export default YhMultiSelect;
